// UltraFlex Admin — Stock Management (Movements, Levels, Stock Takes)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Package, AlertTriangle, ClipboardList, Plus, Check, ArrowDown, ArrowUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminStock() {
  const { token } = useAuth();
  const [tab, setTab] = useState('levels');
  const [levels, setLevels] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stockTakes, setStockTakes] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementForm, setMovementForm] = useState({ product_id: '', site_id: '', quantity_change: 0, movement_type: 'receipt', reason_code: '', notes: '' });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchSites(); }, []);
  useEffect(() => { fetchData(); }, [tab]);

  const fetchSites = async () => {
    const res = await fetch(`${API}/api/sites`, { headers });
    setSites((await res.json()).items || []);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'levels') {
        const [levRes, alertRes] = await Promise.all([
          fetch(`${API}/api/stock/levels`, { headers }),
          fetch(`${API}/api/stock/alerts`, { headers }),
        ]);
        setLevels((await levRes.json()).items || []);
        setAlerts((await alertRes.json()).items || []);
      } else if (tab === 'movements') {
        const res = await fetch(`${API}/api/stock/movements`, { headers });
        setMovements((await res.json()).items || []);
      } else {
        const res = await fetch(`${API}/api/stock/stock-takes`, { headers });
        setStockTakes((await res.json()).items || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const recordMovement = async () => {
    await fetch(`${API}/api/stock/movements`, { method: 'POST', headers, body: JSON.stringify(movementForm) });
    setShowMovementModal(false);
    fetchData();
  };

  const createStockTake = async (siteId) => {
    await fetch(`${API}/api/stock/stock-takes`, { method: 'POST', headers, body: JSON.stringify({ site_id: siteId }) });
    fetchData();
  };

  return (
    <div className="space-y-6" data-testid="stock-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Stock Management</h1>
          <p className="text-sm text-gray-400 mt-1">Inventory levels, movements, and stock-take wizard</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowMovementModal(true)} data-testid="record-movement-btn"
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Record Movement
          </button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-4" data-testid="negative-stock-alert">
          <div className="flex items-center gap-2 text-red-400 font-medium mb-2"><AlertTriangle className="w-4 h-4" /> Negative Stock Alert</div>
          <div className="text-sm text-red-300">{alerts.map(a => `${a.name} (${a.stock_quantity})`).join(', ')}</div>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-800 pb-2">
        {[['levels', 'Stock Levels'], ['movements', 'Movements'], ['stocktakes', 'Stock Takes']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`px-4 py-2 rounded-t text-sm font-medium ${tab === key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : tab === 'levels' ? (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
              <th className="text-left p-3">SKU</th><th className="text-left p-3">Product</th>
              <th className="text-left p-3">Category</th><th className="text-right p-3">Quantity</th>
              <th className="text-center p-3">Status</th>
            </tr></thead>
            <tbody>
              {levels.map(l => (
                <tr key={l.product_id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                  <td className="p-3 font-mono text-xs text-gray-400">{l.sku}</td>
                  <td className="p-3 text-white">{l.name}</td>
                  <td className="p-3 text-gray-400">{l.category}</td>
                  <td className={`p-3 text-right font-medium ${l.is_negative ? 'text-red-400' : l.stock_quantity === 0 ? 'text-yellow-400' : 'text-green-400'}`}>{l.stock_quantity}</td>
                  <td className="p-3 text-center">
                    {l.is_negative ? <span className="text-xs bg-red-900/40 text-red-400 px-2 py-0.5 rounded">Negative</span> :
                     l.stock_quantity === 0 ? <span className="text-xs bg-yellow-900/40 text-yellow-400 px-2 py-0.5 rounded">Out of Stock</span> :
                     <span className="text-xs bg-green-900/40 text-green-400 px-2 py-0.5 rounded">In Stock</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === 'movements' ? (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
              <th className="text-left p-3">Date</th><th className="text-left p-3">Product</th>
              <th className="text-left p-3">Type</th><th className="text-right p-3">Change</th>
              <th className="text-right p-3">Balance</th><th className="text-left p-3">Reason</th>
            </tr></thead>
            <tbody>
              {movements.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-600">No movements recorded</td></tr> :
                movements.map(m => (
                  <tr key={m.id} className="border-b border-gray-800/50">
                    <td className="p-3 text-gray-400 text-xs">{new Date(m.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-white">{m.product_name}</td>
                    <td className="p-3 text-gray-300 capitalize">{m.movement_type}</td>
                    <td className={`p-3 text-right font-medium flex items-center justify-end gap-1 ${m.quantity_change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {m.quantity_change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(m.quantity_change)}
                    </td>
                    <td className="p-3 text-right text-gray-300">{m.balance_after}</td>
                    <td className="p-3 text-gray-500 text-xs">{m.reason_code || '-'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            {sites.filter(s => s.site_type !== 'warehouse').map(s => (
              <button key={s.id} onClick={() => createStockTake(s.id)}
                className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 flex items-center gap-1">
                <ClipboardList className="w-4 h-4" /> New Stock Take — {s.name}
              </button>
            ))}
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-3">Site</th><th className="text-left p-3">Status</th>
                <th className="text-right p-3">Variances</th><th className="text-left p-3">Started</th>
              </tr></thead>
              <tbody>
                {stockTakes.length === 0 ? <tr><td colSpan={4} className="text-center py-8 text-gray-600">No stock takes</td></tr> :
                  stockTakes.map(st => (
                    <tr key={st.id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white">{st.site_name}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${st.status === 'approved' ? 'bg-green-900/40 text-green-400' : st.status === 'submitted' ? 'bg-yellow-900/40 text-yellow-400' : 'bg-blue-900/40 text-blue-400'}`}>{st.status}</span></td>
                      <td className="p-3 text-right text-gray-300">{st.variance_count}</td>
                      <td className="p-3 text-gray-400 text-xs">{st.started_at ? new Date(st.started_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showMovementModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowMovementModal(false)}>
          <div className="bg-[#111] border border-gray-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">Record Stock Movement</h3>
            <div className="space-y-3">
              <select value={movementForm.product_id} onChange={e => setMovementForm({ ...movementForm, product_id: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm">
                <option value="">Select Product</option>
                {levels.map(l => <option key={l.product_id} value={l.product_id}>{l.name} ({l.sku})</option>)}
              </select>
              <select value={movementForm.site_id} onChange={e => setMovementForm({ ...movementForm, site_id: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm">
                <option value="">Select Site</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={movementForm.movement_type} onChange={e => setMovementForm({ ...movementForm, movement_type: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm">
                {['receipt', 'sale', 'adjustment', 'return', 'transfer'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              <input type="number" value={movementForm.quantity_change} onChange={e => setMovementForm({ ...movementForm, quantity_change: parseInt(e.target.value) || 0 })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm" placeholder="Quantity (+/-)" />
              <input value={movementForm.reason_code} onChange={e => setMovementForm({ ...movementForm, reason_code: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm" placeholder="Reason code" />
              <input value={movementForm.notes} onChange={e => setMovementForm({ ...movementForm, notes: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm" placeholder="Notes" />
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setShowMovementModal(false)} className="px-4 py-2 text-gray-400 text-sm">Cancel</button>
              <button onClick={recordMovement} data-testid="submit-movement-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
